import React, { useState } from 'react';
import { post, put, deleteRequest } from 'helpers/api';
import { Panel, Row, Col, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/pro-solid-svg-icons';
import Secondary from '../../../shared/button/secondary';
import Danger from '../../../shared/button/danger';
import JobTitleModal from './job_title_modal';
import ErrorAlert from './error_alert';

export default function JobTitles({ jobTitles, jobTitleableId, jobTitleableType }: JobTitlesProps) {
  const [currentJobTitles, setCurrentJobTitles] = useState(jobTitles);
  const [modalOpen, setModalOpen] = useState(false);
  const [jobTitleToEdit, setJobTitleToEdit] = useState<{ id: number | undefined; label: string }>(
    { id: undefined, label: '' }
  );
  const [error, setError] = useState('');

  const openEditModal = (id: number | undefined, label: string) => {
    setError('');
    setJobTitleToEdit({ id, label });
    setModalOpen(true);
  };

  const createJobTitle = async (jobTitle: JobTitle) => {
    try {
      const res = await post('/job_titles', { jobTitle: { label: jobTitle.label, jobTitleableId, jobTitleableType } });
      if (res.id !== undefined) {
        setCurrentJobTitles([...currentJobTitles, res]);
        closeModal();
      } else {
        setError(res[0]);
      }
      return res.id;
    } catch {
      setError('Job Title could not be created.');
    }
  };

  const updateJobTitle = async (jobTitle) => {
    try {
      const res = await put(`/job_titles/${jobTitle.id}`, { jobTitle: { label: jobTitle.label } });
      if (res.id !== undefined) {
        setCurrentJobTitles(currentJobTitles.map(jt => jt.id === res.id ? res : jt));
        closeModal();
      } else {
        setError(res[0]);
      }
    } catch {
      setError('Job Title could not be created.');
    }
  };

  const deleteJobTitle = async (jobTitleId) => {
    setError('');
    try {
      await deleteRequest(`/job_titles/${jobTitleId}`);
      setCurrentJobTitles(currentJobTitles.filter(jt => jt.id !== jobTitleId));
    } catch {
      setError('Job Title could not be deleted.');
      return null;
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setError('');
    setJobTitleToEdit({ id: undefined, label: '' });
  };

  return (
    <React.Fragment>
      <Modal
        dialogClassName="modal-sm"
        show={modalOpen}
        onHide={closeModal}
      >
        <JobTitleModal
          closeModal={closeModal}
          error={error}
          id={jobTitleToEdit.id}
          label={jobTitleToEdit.label}
          submitForm={jobTitleToEdit.id === null ? createJobTitle : updateJobTitle}
        />
      </Modal>
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <h2 className="m-0">Job Titles</h2>
        <button className="btn btn-primary" onClick={() => openEditModal(undefined, '')}>Add Job Title</button>
      </div>
      <ErrorAlert error={error}/>
      {currentJobTitles.map(jobTitle => (
        <Panel className="card w-100 p-3" key={jobTitle.id}>
          <Row className="row-eq-height" key={jobTitle.id}>
            <Col className="d=flex align-items-center" md={9}>
              <strong>{jobTitle.label}</strong>
            </Col>
            <Col className="d-flex justify-content-end" md={3}>
              <Secondary icon={faPencil} plain type="button" onClick={() => openEditModal(jobTitle.id, jobTitle.label)}>
                Edit
              </Secondary>
              <Danger plain onClick={() => deleteJobTitle(jobTitle.id)}>
                <FontAwesomeIcon icon={faTrash}/>
              </Danger>
            </Col>
          </Row>
        </Panel>
      ))}
    </React.Fragment>
  );
}

type JobTitle = {
  id: number,
  label: string
};

type JobTitlesProps = {
  jobTitleableId: number,
  jobTitleableType: string,
  jobTitles: JobTitle[]
};
